/**
 * Which organization this browser tab is working in.
 *
 * A caller can hold compliance access in more than one organization (a Biogas
 * staffer covering two customers, say). The backend resolves a default — the
 * home organization when it has a grant, otherwise the only grant — and refuses
 * with `ORGANIZATION_SELECTION_REQUIRED` when several are possible and none was
 * named. This module holds what the person chose, and `lib/api.ts` sends it as
 * `X-Organization-Id` on every request.
 *
 * **Not authority.** The backend re-validates the value against the caller's own
 * access rows on every request and refuses one they hold no grant in, so what is
 * stored here can only ever narrow a session to something already permitted —
 * never widen it. That is what makes a plain browser store the right home for
 * it, and it is the same posture `auth/resumeMicrosoftSession.ts` already takes.
 *
 * **`sessionStorage`, deliberately, not `localStorage`.** It is scoped per tab,
 * so switching organization in one tab cannot silently move another — which in
 * a tool that approves regulatory filings is the difference between a
 * convenience and a hazard. The cost is that a new tab starts on the backend's
 * default, which is the safe direction to be wrong in.
 *
 * Every access is wrapped: storage throws outright in some privacy modes, and
 * an unreadable store must degrade to "no selection", never to a broken app.
 */

const STORAGE_KEY = "compliance.organization_id";

/** A well-formed uuid, so a corrupted store cannot put junk on the wire. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readOrgSelection(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const trimmed = raw.trim();
    return UUID_RE.test(trimmed) ? trimmed : null;
  } catch {
    return null;
  }
}

export function writeOrgSelection(organizationId: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, organizationId);
  } catch {
    // Storage is unavailable or full. The selection then lasts as long as the
    // page does, which is worse than persisting it but not wrong: the request
    // that follows still carries the header, so the choice takes effect now and
    // is simply asked for again after a reload.
  }
}

export function clearOrgSelection(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — see writeOrgSelection.
  }
}
